import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo } from "react";
import {
  PAGE_SIZE, PRICE_MAX, PRICE_MIN, filterAndSort,
  type CatalogFilters, type SortValue,
} from "@/lib/catalog/products";
import { getCatalogRepository } from "@/lib/catalog/repository";
import { CatalogToolbar } from "@/components/catalog/CatalogToolbar";
import { CatalogGrid } from "@/components/catalog/CatalogGrid";
import { EmptyState, ProductGridSkeleton } from "@/components/catalog/CatalogStates";
import { CategoryTabs } from "@/components/catalog/CategoryTabs";
import { canonicalUrl } from "@/lib/seo/site";
import { breadcrumbSchema, itemListSchema, ldScript } from "@/lib/seo/schema";


const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  min: fallback(z.number(), PRICE_MIN).default(PRICE_MIN),
  max: fallback(z.number(), PRICE_MAX).default(PRICE_MAX),
  sort: fallback(z.string(), "featured").default("featured"),
  page: fallback(z.number(), 1).default(1),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/shop/")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const products = await getCatalogRepository().getProducts();
    return { products };
  },
  pendingComponent: () => <ProductGridSkeleton />,
  head: ({ loaderData }) => {
    const title = "Shop All Fragrances | CB16";
    const description =
      "Explore every CB16 scent — perfumes, car scents, diffusers, oils, and more, from a single catalog.";
    const url = canonicalUrl("/shop");
    const products = loaderData?.products ?? [];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
          ]),
        ),
        ...(products.length ? [ldScript(itemListSchema(products))] : []),
      ],
    };
  },

  component: ShopAllPage,
});

function ShopAllPage() {
  const { products } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const filters: CatalogFilters = useMemo(() => ({
    q: search.q,
    families: [],
    genders: [],
    concentrations: [],
    collection: "",
    min: search.min,
    max: search.max,
    sort: (search.sort as SortValue) ?? "featured",
  }), [search]);

  const results = useMemo(() => filterAndSort(products, filters), [products, filters]);
  const visibleCount = Math.min(results.length, search.page * PAGE_SIZE);
  const visible = results.slice(0, visibleCount);
  const canLoadMore = visibleCount < results.length;

  function applyPatch(patch: Partial<CatalogFilters>) {
    navigate({ search: (prev: Search) => ({
      ...prev,
      q: patch.q ?? prev.q,
      min: patch.min ?? prev.min,
      max: patch.max ?? prev.max,
      sort: patch.sort ?? prev.sort,
      page: 1,
    }), replace: true });
  }
  function reset() {
    navigate({ search: { q: "", min: PRICE_MIN, max: PRICE_MAX, sort: "featured", page: 1 }, replace: true });
  }
  function loadMore() {
    navigate({ search: (prev: Search) => ({ ...prev, page: prev.page + 1 }), replace: true });
  }

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">The Maison</span>
          <h1 className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl" style={{ fontFamily: "'Playfair Display', serif" }}>Shop All Products</h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/60 sm:text-base">Nine scent categories, one house. Every CB16 creation, from personal perfumes to home diffusers, gathered in a single catalog.</p>
        </div>
      </section>

      <CategoryTabs activeSlug={null} />

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <CatalogToolbar filters={filters} totalCount={results.length} onChange={applyPatch} onReset={reset} />
        <div className="mt-10">
          {results.length === 0 && <EmptyState onReset={reset} />}
          {results.length > 0 && (
            <>
              <CatalogGrid products={visible} />
              {canLoadMore && (
                <div className="mt-16 flex justify-center">
                  <button type="button" onClick={loadMore} className="border border-black px-8 py-3 text-[11px] font-medium uppercase tracking-[0.28em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]">
                    Load More ({results.length - visibleCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}